import React, { useEffect, useRef, useState } from "react";
import { VideoQuality } from "@zoom/videosdk";
import "./ZoomVideoPlayer.css";

const ATTACH_RETRY_DELAYS = [300, 900];
const MAX_FALLBACK_CANVAS_WIDTH = 1920;
const MAX_FALLBACK_CANVAS_HEIGHT = 1080;

const isVideoPlayerElement = (value) => (
    typeof HTMLElement !== "undefined" && value instanceof HTMLElement
);

const supportsVideoPlayer = (stream) => {
    if (!stream || typeof stream.attachVideo !== "function") return false;

    try {
        return typeof stream.isSupportMultipleVideos !== "function"
            || stream.isSupportMultipleVideos();
    } catch (error) {
        console.debug("Unable to determine Zoom multi-video support:", error);
        return false;
    }
};

const usesVideoElementForSelfView = (stream, isSelf) => {
    if (!isSelf || typeof stream?.isRenderSelfViewWithVideoElement !== "function") return false;

    try {
        return stream.isRenderSelfViewWithVideoElement();
    } catch (error) {
        console.debug("Unable to determine Zoom self-view renderer:", error);
        return false;
    }
};

const getFallbackCanvasSize = (element) => {
    const bounds = element.getBoundingClientRect();
    const pixelRatio = Math.min(Math.max(window.devicePixelRatio || 1, 1), 2);
    const requestedWidth = Math.max(1, Math.round(bounds.width * pixelRatio));
    const requestedHeight = Math.max(1, Math.round(bounds.height * pixelRatio));
    const scale = Math.min(
        1,
        MAX_FALLBACK_CANVAS_WIDTH / requestedWidth,
        MAX_FALLBACK_CANVAS_HEIGHT / requestedHeight,
    );

    return {
        bounds,
        width: Math.max(1, Math.round(requestedWidth * scale)),
        height: Math.max(1, Math.round(requestedHeight * scale)),
    };
};

function ZoomVideoPlayer({
    stream,
    userId,
    videoOn,
    quality = VideoQuality.Video_720P,
    className = "",
    style,
    retryToken = 0,
    label = "Zoom video",
    isSelf = false,
    fit = "contain",
    aspectRatio = 16 / 9,
}) {
    const frameRef = useRef(null);
    const playerRef = useRef(null);
    const fallbackRef = useRef(null);
    const [renderState, setRenderState] = useState("idle");
    const [forceFallback, setForceFallback] = useState(false);
    const canAttachVideo = supportsVideoPlayer(stream) && !forceFallback;
    const useVideoElement = !canAttachVideo && usesVideoElementForSelfView(stream, isSelf);

    useEffect(() => {
        setForceFallback(false);
    }, [retryToken, stream, userId, videoOn]);

    useEffect(() => {
        if (fit !== "cover") return undefined;

        const frame = frameRef.current;
        const mediaElement = canAttachVideo ? playerRef.current : fallbackRef.current;
        if (!frame || !mediaElement) return undefined;

        const sourceAspectRatio = Number.isFinite(aspectRatio) && aspectRatio > 0
            ? aspectRatio
            : 16 / 9;

        const updateCoverSize = () => {
            const { width: frameWidth, height: frameHeight } = frame.getBoundingClientRect();
            if (frameWidth < 1 || frameHeight < 1) return;

            if (frameWidth / frameHeight > sourceAspectRatio) {
                mediaElement.style.width = `${Math.ceil(frameWidth)}px`;
                mediaElement.style.height = `${Math.ceil(frameWidth / sourceAspectRatio)}px`;
            } else {
                mediaElement.style.width = `${Math.ceil(frameHeight * sourceAspectRatio)}px`;
                mediaElement.style.height = `${Math.ceil(frameHeight)}px`;
            }
            mediaElement.style.aspectRatio = String(sourceAspectRatio);
        };

        updateCoverSize();
        const resizeObserver = typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(updateCoverSize)
            : null;
        resizeObserver?.observe(frame);
        window.addEventListener("resize", updateCoverSize);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener("resize", updateCoverSize);
            mediaElement.style.width = "";
            mediaElement.style.height = "";
            mediaElement.style.aspectRatio = "";
        };
    }, [aspectRatio, canAttachVideo, fit]);

    useEffect(() => {
        const renderElement = canAttachVideo ? playerRef.current : fallbackRef.current;
        if (!stream || !Number.isFinite(userId) || !videoOn || !renderElement) {
            setRenderState("idle");
            return undefined;
        }

        let cancelled = false;
        let rendering = false;
        let retryTimer;
        let resizeObserver;
        let attempt = 0;

        const stopRendering = async () => {
            if (!rendering) return;
            rendering = false;
            try {
                if (canAttachVideo) {
                    await stream.detachVideo(userId, renderElement);
                } else {
                    await stream.stopRenderVideo(renderElement, userId);
                }
            } catch (error) {
                console.debug(`Unable to stop rendering Zoom video for user ${userId}:`, error);
            }
        };

        const scheduleRetry = (render) => {
            if (attempt >= ATTACH_RETRY_DELAYS.length) return false;
            const delay = ATTACH_RETRY_DELAYS[attempt];
            attempt += 1;
            retryTimer = window.setTimeout(render, delay);
            return true;
        };

        const renderWithVideoPlayer = async () => {
            if (cancelled) return;
            setRenderState("attaching");

            try {
                const result = await stream.attachVideo(userId, quality, renderElement);
                if (result && !isVideoPlayerElement(result)) throw result;

                if (cancelled) {
                    await stream.detachVideo(userId, renderElement).catch(() => undefined);
                    return;
                }

                rendering = true;
                setRenderState("attached");
            } catch (error) {
                if (cancelled) return;
                if (scheduleRetry(renderWithVideoPlayer)) return;

                console.warn(`Zoom video-player rendering failed for user ${userId}; using canvas fallback.`, error);
                setForceFallback(true);
            }
        };

        const renderWithCanvas = async () => {
            if (cancelled) return;
            setRenderState("attaching");

            const isVideoElement = typeof HTMLVideoElement !== "undefined"
                && renderElement instanceof HTMLVideoElement;
            const { bounds, width, height } = getFallbackCanvasSize(renderElement);

            if (!isVideoElement && (bounds.width < 1 || bounds.height < 1)) {
                if (scheduleRetry(renderWithCanvas)) return;
                setRenderState("error");
                return;
            }

            if (!isVideoElement) {
                renderElement.width = width;
                renderElement.height = height;
            }

            try {
                await stream.renderVideo(
                    renderElement,
                    userId,
                    isVideoElement ? undefined : width,
                    isVideoElement ? undefined : height,
                    isVideoElement ? undefined : 0,
                    isVideoElement ? undefined : 0,
                    quality,
                );

                if (cancelled) {
                    await stream.stopRenderVideo(renderElement, userId).catch(() => undefined);
                    return;
                }

                rendering = true;
                setRenderState("attached");

                if (!isVideoElement && typeof ResizeObserver !== "undefined") {
                    resizeObserver = new ResizeObserver(() => {
                        if (!rendering || cancelled) return;
                        const { width: nextWidth, height: nextHeight } = getFallbackCanvasSize(renderElement);
                        if (renderElement.width === nextWidth && renderElement.height === nextHeight) return;

                        try {
                            if (typeof stream.updateVideoCanvasDimension === "function") {
                                stream.updateVideoCanvasDimension(renderElement, nextWidth, nextHeight);
                            } else {
                                renderElement.width = nextWidth;
                                renderElement.height = nextHeight;
                            }
                            stream.adjustRenderedVideoPosition?.(
                                renderElement,
                                userId,
                                nextWidth,
                                nextHeight,
                                0,
                                0,
                            );
                        } catch (error) {
                            console.debug(`Unable to resize Zoom canvas for user ${userId}:`, error);
                        }
                    });
                    resizeObserver.observe(renderElement);
                }
            } catch (error) {
                if (cancelled) return;
                if (scheduleRetry(renderWithCanvas)) return;

                setRenderState("error");
                console.error(`Unable to render Zoom video for user ${userId}:`, error);
            }
        };

        if (canAttachVideo) {
            renderWithVideoPlayer();
        } else {
            renderWithCanvas();
        }

        return () => {
            cancelled = true;
            window.clearTimeout(retryTimer);
            resizeObserver?.disconnect();
            stopRendering();
        };
    }, [canAttachVideo, quality, retryToken, stream, useVideoElement, userId, videoOn]);

    return (
        <div
            ref={frameRef}
            className={`zoom-video-player ${fit === "cover" ? "zoom-video-player--cover" : ""} ${className}`.trim()}
            style={style}
            data-render-state={renderState}
            aria-label={label}
        >
            {canAttachVideo ? (
                <video-player-container class="zoom-video-player__container">
                    <video-player ref={playerRef} class="zoom-video-player__media" />
                </video-player-container>
            ) : useVideoElement ? (
                <video ref={fallbackRef} className="zoom-video-player__media" autoPlay muted playsInline />
            ) : (
                <canvas ref={fallbackRef} className="zoom-video-player__media" />
            )}
            {renderState === "attaching" && (
                <div className="zoom-video-player__status" role="status">Connecting video...</div>
            )}
            {renderState === "error" && (
                <div className="zoom-video-player__status zoom-video-player__status--error" role="alert">
                    Video could not be displayed
                </div>
            )}
        </div>
    );
}

export default ZoomVideoPlayer;
